"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Intelligence() {
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [particles, setParticles] = useState<Array<{
    id: number;
    left: number;
    delay: number;
    duration: number;
    size: number;
  }>>([]);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const generated = Array.from({
      length: 24
    }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 12 + Math.random() * 10,
      size: 1 + Math.random() * 3
    }));
    setParticles(generated);
  }, []);
  const stats = [{
    label: "CLASS",
    value: "RETEACH GUIDANCE"
  }, {
    label: "STUDENT",
    value: "PERSONALISED FEEDBACK"
  }, {
    label: "MARKS",
    value: "LOST BREAKDOWN"
  }, {
    label: "TEACHER",
    value: "ZERO EXTRA GRADING"
  }];
  return <section data-section-id="228" id="departmental-intelligence" ref={sectionRef} className="relative bg-background text-foreground overflow-hidden min-h-[90vh] flex items-end">
      {/* Subtle particle field */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => <span key={p.id} data-index={i} className="absolute rounded-lg bg-primary/30" style={{
        left: `${p.left}%`,
        bottom: `-10px`,
        width: `${p.size}px`,
        height: `${p.size}px`,
        animation: `floatUp ${p.duration}s linear ${p.delay}s infinite`
      }} />)}
      </div>

      {/* Vertical Rail Text */}
      <div className="hidden lg:block absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        <p className="font-mono text-[10px] tracking-[0.3em] text-foreground/50 uppercase whitespace-nowrap" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          DIAGNOSTIC &middot; MAPPING &middot; LOGIC &middot; ACCURACY
        </p>
      </div>

      {/* Content Container */}
      <div ref={contentRef} className={`relative z-10 w-full px-6 md:px-12 lg:px-20 xl:pl-[120px] xl:pr-20 pt-32 pb-16 md:pb-20 lg:pb-24 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-8 md:mb-10">
          <span className="block w-12 h-px bg-primary" />
          <p className="font-mono text-xs tracking-[0.25em] text-primary uppercase">
            SETURA &middot; DIAGNOSTIC ENGINE
          </p>
        </div>

        {/* Headline */}
        <h1 className="font-default font-semibold text-foreground leading-[0.85] tracking-tighter mb-10 md:mb-12" style={{
        fontSize: "clamp(60px, 14vw, 160px)"
      }}>
          <span className="block">KNOW WHAT</span>
          <span className="block">TO RETEACH.</span>
          <span className="block italic text-primary">KNOW WHO NEEDS HELP.</span>
        </h1>

        {/* Narrative Copy */}
        <p className="font-default text-base md:text-lg leading-relaxed text-foreground/80 max-w-[560px] mb-10 md:mb-12">
          Setura reads the assessments students already produce, homework, classwork, mocks, and turns them into class-level reteach guidance plus individual student feedback, without teachers having to manually analyse every script.
        </p>

        {/* Diagnostic Coordinates Grid */}
        <div className="mb-10 md:mb-12 max-w-3xl">
          <div className="flex flex-wrap items-stretch gap-y-4 border-t border-b border-border py-5">
            {stats.map((stat, i) => <div key={stat.label} data-index={i} className={`flex-1 min-w-[140px] px-4 md:px-5 ${i !== 0 ? "md:border-l border-border" : ""}`}>
                <p className="font-mono text-[10px] tracking-[0.2em] text-foreground/50 uppercase mb-1.5">
                  {stat.label}
                </p>
                <p className="font-mono text-sm md:text-base font-medium text-foreground tracking-wider">
                  {stat.value}
                </p>
              </div>)}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-4">
          <Button asChild size="lg" className="h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-[0.2em] uppercase rounded-lg shadow-lg">
            <Link to="/SeturaPage">
              Explore Setura
              <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-14 px-8 bg-transparent border border-border text-foreground hover:bg-accent hover:text-accent-foreground font-mono text-xs tracking-[0.2em] uppercase rounded-lg">
            <Link to="/PilotPage">
              <Users className="mr-1 size-4" />
              Book a pilot call
            </Link>
          </Button>
        </div>
      </div>

      {/* Animation Keyframes */}
      <style>{`
        @keyframes floatUp {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          15% {
            opacity: 0.6;
          }
          85% {
            opacity: 0.4;
          }
          100% {
            transform: translateY(-105vh) translateX(20px);
            opacity: 0;
          }
        }
      `}</style>
    </section>;
}